import { Component, OnInit, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '../../api.service';
import { CommonService } from '../../shared/common.service';

@Component({
  selector: 'app-query-view',
  templateUrl: './query-view.component.html',
  styleUrls: ['./query-view.component.scss'],
})
export class QueryViewComponent implements OnInit {
  selectedStudyId = '';
  selectedStudyName = '';
  availableStudies: any[] = [];
  filteredStudies: any[] = [];
  isStudyDropdownOpen = false;
  studySearchText = '';

  loading = false;
  data: any[] = [];
  filteredData: any[] = [];
  paginatedData: any[] = [];
  currentPage = 1;
  pageSize = 10;
  pages = 0;
  totalUsers = 0;
  pageRange: number[] = [];
  maxPagesToShow = 4;
  activeTabCount = 0;

  // Filters (same as Data View)
  queryIdFilter = '';
  participantFilter = '';
  startDateFilter = '';
  endDateFilter = '';
  availableQueryIds: string[] = [];
  availableParticipants: string[] = [];
  availableStudyIds: string[] = [];

  isExportingCSV = false;

  // Tabs and role-based controls
  activeTab: 'all' | 'open' | 'answered' | 'closed' = 'all';
  userRole = '';
  isMonitor = false;
  selectedQueryIds: Set<string | number> = new Set<string | number>();
  isBulkClosing = false;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private commonService: CommonService
  ) {}

  ngOnInit(): void {
    const userData = this.commonService.getUserData();
    const designation = (userData?.designation || '').toString().trim().toLowerCase();
    this.userRole = designation;
    this.isMonitor = designation === 'monitor';
    this.loadAvailableStudies();
  }

  loadAvailableStudies(): void {
    const userData = this.commonService.getUserData();
    if (userData?.id) {
      this.apiService.getStudiesCreatedByUser(userData.id).subscribe({
        next: (response: any) => {
          this.availableStudies = response.data || [];
          this.filteredStudies = [...this.availableStudies];
        },
        error: (err) => {
          console.error('Error loading studies:', err);
          this.availableStudies = [];
          this.filteredStudies = [];
        },
      });
    }
  }

  toggleStudyDropdown(): void {
    this.isStudyDropdownOpen = !this.isStudyDropdownOpen;
    if (this.isStudyDropdownOpen) {
      this.filteredStudies = [...this.availableStudies];
      this.studySearchText = '';
    }
  }

  filterStudies(): void {
    if (!this.studySearchText.trim()) {
      this.filteredStudies = [...this.availableStudies];
    } else {
      this.filteredStudies = this.availableStudies.filter((study) => {
        const name = (study.name || study.study_name || '').toLowerCase();
        return name.includes(this.studySearchText.toLowerCase());
      });
    }
  }

  selectStudy(study: any): void {
    this.selectedStudyId = String(study.id ?? study.study_id);
    this.selectedStudyName = study.name || study.study_name;
    this.isStudyDropdownOpen = false;
    this.studySearchText = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown-container')) {
      this.isStudyDropdownOpen = false;
    }
  }

  submitStudySelection(): void {
    if (!this.selectedStudyId) return;
    this.loading = true;
    this.currentPage = 1;
    this.selectedQueryIds.clear();
    this.apiService
      .getStudyQueries(this.selectedStudyId, { page: 1, limit: 10000 })
      .subscribe({
        next: (response: any) => {
          this.data = response.data || [];
          this.populateFilterOptions();
          this.applyFilters();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  updatePaginatedData(page: number): void {
    const start = (page - 1) * this.pageSize;
    this.paginatedData = this.filteredData.slice(start, start + this.pageSize);
    this.updatePageRange();
  }

  updatePageRange(): void {
    this.pageRange = [];
    const startPage =
      Math.floor((this.currentPage - 1) / this.maxPagesToShow) *
        this.maxPagesToShow +
      1;
    const endPage = Math.min(startPage + this.maxPagesToShow - 1, this.pages);
    for (let i = startPage; i <= endPage; i++) this.pageRange.push(i);
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedData(this.currentPage);
    }
  }

  nextPage(): void {
    if (this.currentPage < this.pages) {
      this.currentPage++;
      this.updatePaginatedData(this.currentPage);
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.pages) {
      this.currentPage = page;
      this.updatePaginatedData(this.currentPage);
    }
  }

  populateFilterOptions(): void {
    if (!this.data || this.data.length === 0) return;

    this.availableQueryIds = [
      ...new Set(
        this.data
          .map((item) => item.unique_query_id ?? item.query_id)
          .filter((id) => id != null && String(id).trim() !== '')
      ),
    ].sort((a, b) => String(a).localeCompare(String(b)));

    this.availableParticipants = [
      ...new Set(
        this.data
          .map((item) => item.subject_id)
          .filter((s) => s != null && String(s).trim() !== '')
      ),
    ].sort();

    this.availableStudyIds = [
      ...new Set(
        this.data
          .map((item) => item.study_id)
          .filter((id) => id != null && String(id).trim() !== '')
      ),
    ].sort((a, b) => String(a).localeCompare(String(b)));
  }

  applyFilters(): void {
    let result = [...this.data];

    if (this.queryIdFilter && this.queryIdFilter !== '') {
      result = result.filter(
        (row) => {
          const id = row.unique_query_id ?? row.query_id;
          return (
            id != null &&
            String(id).toLowerCase().includes(this.queryIdFilter.toLowerCase())
          );
        }
      );
    }
    if (this.participantFilter && this.participantFilter !== '') {
      result = result.filter(
        (row) =>
          row.subject_id &&
          String(row.subject_id).toLowerCase().includes(this.participantFilter.toLowerCase())
      );
    }
    // Filter by Query Opened Date range, if provided
    if ((this.startDateFilter && this.startDateFilter !== '') || (this.endDateFilter && this.endDateFilter !== '')) {
      const start = this.startDateFilter ? new Date(this.startDateFilter) : null;
      const end = this.endDateFilter ? new Date(this.endDateFilter) : null;

      // Normalize end date to end of the day to make range inclusive
      if (end) {
        end.setHours(23, 59, 59, 999);
      }

      result = result.filter((row) => {
        if (!row.query_opened_date) {
          return false;
        }
        const opened = new Date(row.query_opened_date);
        if (isNaN(opened.getTime())) {
          return false;
        }

        if (start && opened < start) {
          return false;
        }
        if (end && opened > end) {
          return false;
        }
        return true;
      });
    }

    // Apply tab-based status filtering on top of existing filters
    if (this.activeTab === 'open') {
      // Open tab: queries that are not closed and have NO response yet
      result = result.filter((row) => {
        const status = (row.query_status || row.queryStatus || '')
          .toString()
          .toLowerCase();
        const response =
          row.query_response != null ? String(row.query_response).trim() : '';
        return status !== 'closed' && response === '';
      });
    } else if (this.activeTab === 'answered') {
      // Answered tab: queries that are still open but have a response
      result = result.filter((row) => {
        const status = (row.query_status || row.queryStatus || '')
          .toString()
          .toLowerCase();
        const response =
          row.query_response != null ? String(row.query_response).trim() : '';
        return status === 'open' && response !== '';
      });
    } else if (this.activeTab === 'closed') {
      result = result.filter(
        (row) =>
          (row.query_status || row.queryStatus || '')
            .toString()
            .toLowerCase() === 'closed'
      );
    }

    this.filteredData = result;
    this.activeTabCount = this.filteredData.length;
    this.pages = Math.ceil(this.filteredData.length / this.pageSize);
    this.totalUsers = this.filteredData.length;
    this.currentPage = 1;
    this.updatePaginatedData(this.currentPage);
  }

  clearFilters(): void {
    this.queryIdFilter = '';
    this.participantFilter = '';
    this.startDateFilter = '';
    this.endDateFilter = '';
    this.selectedQueryIds.clear();
    this.applyFilters();
  }

  setActiveTab(tab: 'all' | 'open' | 'answered' | 'closed'): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    // When switching tabs, clear any selections to avoid confusion
    this.selectedQueryIds.clear();
    this.applyFilters();
  }

  isQuerySelected(row: any): boolean {
    const id = row.unique_query_id ?? row.query_id;
    if (id == null) return false;
    return this.selectedQueryIds.has(String(id)) || this.selectedQueryIds.has(id);
  }

  toggleQuerySelection(row: any, checked: boolean): void {
    const id = row.unique_query_id ?? row.query_id;
    if (id == null) return;
    const key = typeof id === 'number' ? id : String(id);
    if (checked) {
      this.selectedQueryIds.add(key);
    } else {
      this.selectedQueryIds.delete(key);
    }
  }

  closeSelectedQueries(): void {
    if (!this.isMonitor || this.selectedQueryIds.size === 0 || !this.selectedStudyId) {
      return;
    }
    this.isBulkClosing = true;
    const payload = {
      studyId: this.selectedStudyId,
      queryIds: Array.from(this.selectedQueryIds),
    };
    this.apiService.closeStudyQueriesBulk(payload).subscribe({
      next: (response: any) => {
        const message =
          response?.message ||
          `Successfully closed ${response?.closedCount ?? this.selectedQueryIds.size} queries.`;
        alert(message);

        // Optimistic UI update: mark closed queries as Closed in local data
        const idsToClose = new Set(
          payload.queryIds.map((id) => (typeof id === 'number' ? id : String(id)))
        );
        this.data = this.data.map((row) => {
          const id = row.unique_query_id ?? row.query_id;
          const key = id == null ? null : (typeof id === 'number' ? id : String(id));
          if (key != null && idsToClose.has(key)) {
            return { ...row, query_status: 'Closed' };
          }
          return row;
        });

        this.selectedQueryIds.clear();
        this.applyFilters();
      },
      error: (error) => {
        console.error('Error closing queries:', error);
        alert(error?.error?.message || 'Error closing queries. Please try again.');
      },
      complete: () => {
        this.isBulkClosing = false;
      },
    });
  }

  downloadCSV(): void {
    if (!this.selectedStudyId || this.filteredData.length === 0) {
      alert('No data available to download. Please select a study and ensure data is loaded.');
      return;
    }
    this.isExportingCSV = true;
    try {
      const headers = [
        '#',
        'Query ID',
        'Query Age',
        'Study ID',
        'Participant ID',
        'Site Location',
        'Query Open',
        'Query Opened Date',
        'Opened by & Role',
        'Response by & Role',
        'Closed by & Role',
        'Query Response',
        'Query Response Date',
        'Form Name',
        'Field Name',
      ];
      const formatDate = (d: string | null | undefined) =>
        d ? new Date(d).toISOString() : 'N/A';
      const escape = (v: any) =>
        v != null ? String(v).replace(/"/g, '""') : 'N/A';
      const rows = this.filteredData.map((item, index) => [
        index + 1,
        item.unique_query_id ?? item.query_id ?? 'N/A',
        this.getQueryAge(item.query_opened_date),
        item.study_id ?? 'N/A',
        item.subject_id ?? 'N/A',
        item.site_location ?? 'N/A',
        escape(item.query_open ?? item.query),
        formatDate(item.query_opened_date),
        escape(item.opened_by_role),
        escape(item.response_by_role),
        escape(item.closed_by_role),
        escape(item.query_response),
        formatDate(item.query_response_date),
        item.form_name ?? 'N/A',
        item.field_name ?? 'N/A',
      ]);
      const csvContent = [headers, ...rows]
        .map((e) => e.map((v) => `"${v}"`).join(','))
        .join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const fileName = `Query_View_${(this.selectedStudyName || 'Study').replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating CSV:', error);
      alert('Error generating CSV. Please try again.');
    } finally {
      this.isExportingCSV = false;
    }
  }

  /**
   * Compute human-readable query age from opened date to now, e.g. "3 Days", "1 Month 2 Days", "Today".
   */
  getQueryAge(openedDate: string | null | undefined): string {
    if (!openedDate) {
      return 'N/A';
    }
    const opened = new Date(openedDate);
    if (isNaN(opened.getTime())) {
      return 'N/A';
    }
    const now = new Date();
    let diffMs = now.getTime() - opened.getTime();
    if (diffMs < 0) {
      diffMs = 0;
    }
    const diffDaysTotal = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDaysTotal === 0) {
      return 'Today';
    }
    const months = Math.floor(diffDaysTotal / 30);
    const days = diffDaysTotal % 30;
    const parts: string[] = [];
    if (months > 0) {
      parts.push(`${months} Month${months > 1 ? 's' : ''}`);
    }
    if (days > 0) {
      parts.push(`${days} Day${days > 1 ? 's' : ''}`);
    }
    return parts.join(' ');
  }

  openQueryModal(row: any): void {
    // Ensure we have the minimum data required to redirect meaningfully
    const templateId = row.template_id;
    const studyId = row.study_id;
    const subjectId = row.subject_id;

    if (!templateId || !studyId || !subjectId) {
      console.error('[QUERY-VIEW] Missing templateId / studyId / subjectId for redirect:', {
        templateId,
        studyId,
        subjectId,
        row,
      });
      return;
    }

    // Pre-select the correct form in StudyTemplate by priming its "last opened form" storage key
    const storageKey = `studyTemplate:lastForm:${templateId}:${studyId}:${subjectId}`;
    const lastFormPayload = {
      formId: row.form_id ?? null,
      formName: row.form_name ?? null,
      folderId: null,
      folderName: null,
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(lastFormPayload));
    } catch (error) {
      console.warn('[QUERY-VIEW] Failed to persist last opened form payload:', error);
    }

    // Navigate to Study Template with all known context
    this.router.navigate(['/study-template'], {
      queryParams: {
        studyId: studyId,
        subjectId: subjectId,
        // For StudyTemplateComponent mapping
        study_id: studyId,
        template_id: templateId,
        study_name: this.selectedStudyName,
        study_code: row.study_code || '',
        site_location: row.site_location || '',
        subject_id: subjectId,
      },
    });
  }
}
